import React, { useRef } from 'react';
import VideoPlayer from '../Common/VideoPlayer';
import { confirmDialog } from '../Common/confirm';

/* Редактор таймкодов записи. Если запись — прямое видео, показываем плеер:
   можно поймать текущий момент кнопкой. Время можно и вписать вручную (ММ:СС). */

const isDirectVideo = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    if (/\.(mp4|webm|ogg|ogv|mov|m4v)$/i.test(u.pathname)) return true;
    if (/(yandexcloud\.net|storage\.yandex|\.s3\.|s3\.|timeweb\.cloud)/i.test(u.hostname)) return true;
  } catch { return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|$)/i.test(url); }
  return false;
};

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return (h > 0 ? `${h}:` : '') + `${mm}:${String(sec).padStart(2, '0')}`;
};
const parseTime = (str) => {
  const parts = String(str).trim().split(':').map(p => Number(p));
  if (!parts.length || parts.some(p => isNaN(p) || p < 0)) return null;
  return parts.reduce((acc, p) => acc * 60 + p, 0);
};

let _uid = 0;
const withId = (c) => ({ id: c.id ?? `tc${++_uid}`, t: c.t || 0, title: c.title || '' });

export default function TimecodeEditor({ recordLink, value, onChange }) {
  const playerRef = useRef(null);
  const direct = isDirectVideo(recordLink);
  const list = (value || []).map(withId);

  const push = (item) => onChange([...list, withId(item)]);
  const addCurrent = () => push({ t: Math.floor(playerRef.current?.getTime?.() || 0), title: '' });
  const addManual = () => push({ t: 0, title: '' });
  const update = (id, patch) => onChange(list.map(c => c.id === id ? { ...c, ...patch } : c));
  const remove = async (id) => { if (await confirmDialog({ title: 'Удалить таймкод?', message: 'Этот таймкод будет удалён.', confirmText: 'Удалить' })) onChange(list.filter(c => c.id !== id)); };

  const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

  return (
    <div>
      {direct ? (
        <div style={{ marginBottom: 12 }}>
          <VideoPlayer ref={playerRef} src={recordLink} />
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 10 }}>
          Запись не прямой ссылкой — времена вписывайте вручную (МM:СС).
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {direct && (
          <button type="button" className="tc-add-cur" style={{ ...btn, borderColor: 'var(--primary)', color: 'var(--primary)' }} onClick={addCurrent}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
            Добавить таймкод (текущий момент)
          </button>
        )}
        <button type="button" style={btn} onClick={addManual}>+ Вручную</button>
      </div>

      {list.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '6px 0' }}>Таймкодов пока нет.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                defaultValue={fmt(c.t)}
                onBlur={(e) => { const s = parseTime(e.target.value); if (s != null) update(c.id, { t: s }); else e.target.value = fmt(c.t); }}
                style={{ width: 78, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, textAlign: 'center', fontVariantNumeric: 'tabular-nums', background: 'var(--bg)', color: 'var(--text-1)' }}
                title="Время МM:СС"
              />
              {direct && (
                <button type="button" title="Перейти к этому моменту" onClick={() => playerRef.current?.seekTo?.(c.t)}
                  style={{ width: 34, height: 34, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 4 20 12 6 20 6 4"/></svg>
                </button>
              )}
              <input
                value={c.title}
                onChange={(e) => update(c.id, { title: e.target.value })}
                placeholder="Название главы"
                style={{ flex: 1, minWidth: 120, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13.5, background: 'var(--bg)', color: 'var(--text-1)' }}
              />
              <button type="button" title="Удалить" onClick={() => remove(c.id)}
                style={{ width: 34, height: 34, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fecaca', borderRadius: 8, background: 'rgba(239,68,68,0.13)', color: '#dc2626', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
